import {
  bindThemeParamsCssVars,
  copyTextToClipboard,
  downloadFile,
  openInvoice,
  shareURL,
  init,
  isTMA,
  mountThemeParamsSync,
  retrieveRawInitData,
} from '@telegram-apps/sdk-react';

/**
 * Единственное место, где приложение разговаривает с Telegram (ТЗ §4, §5).
 *
 * Изолировано намеренно: SDK меняет API между мажорными версиями, а вся остальная кодовая
 * база должна знать про Telegram ровно две вещи — сырую `initData` и язык клиента.
 *
 * `initData` НЕ разбирается на пользователя ради авторизации. Клиента определяет бэкенд,
 * проверив HMAC-подпись (`actorTgId` в `routes/public.ts`). Значение `user.id`, прочитанное
 * здесь, доказательством не является и потому не используется нигде, кроме выбора локали.
 */

export interface TelegramContext {
  /** Приложение открыто из Telegram: есть launch-параметры и подписанная `initData`. */
  readonly inTelegram: boolean;
  /**
   * Сырая `initData` для заголовка `Authorization: tma …`. `null` вне Telegram —
   * и тогда защищённые запросы честно отказывают ключом `err.noInitData`, а не идут
   * на сервер без подписи, чтобы получить невнятный 401.
   */
  readonly rawInitData: string | null;
  /** `language_code` пользователя Telegram. Только для выбора стартовой локали. */
  readonly languageCode: string | undefined;
  /** `start_param` из deep-link: им приходит slug организации при входе по QR (ТЗ §5). */
  readonly startParam: string | undefined;
  /**
   * Что помешало инициализации, если она не удалась внутри Telegram. Не `null`-заглушка:
   * сообщение показывается на экране, иначе «приложение не грузится» останется загадкой.
   */
  readonly setupError: string | null;
}

const OUTSIDE_TELEGRAM: TelegramContext = {
  inTelegram: false,
  rawInitData: null,
  languageCode: undefined,
  startParam: undefined,
  setupError: null,
};

/**
 * Инициализация SDK и привязка темы.
 *
 * `bindThemeParamsCssVars()` публикует переменные `--tg-theme-*`, на которых построена
 * палитра (`src/index.css`, ТЗ §14.3). Без него приложение осталось бы на значениях
 * по умолчанию и не следовало бы за темой клиента.
 *
 * Вызывается один раз до отрисовки: тема, применённая после первого кадра, даёт заметное
 * мигание светлым по тёмному.
 */
export function setupTelegram(): TelegramContext {
  if (!isTMA()) return OUTSIDE_TELEGRAM;

  try {
    init();

    // `.isAvailable()` — не «на всякий случай»: старые клиенты Telegram не поддерживают
    // тему, и вызов на них бросает. Это не отказ приложения — палитра остаётся на
    // значениях по умолчанию из index.css, они читаемы.
    if (mountThemeParamsSync.isAvailable()) mountThemeParamsSync();
    if (bindThemeParamsCssVars.isAvailable()) bindThemeParamsCssVars();

    const rawInitData = retrieveRawInitData() ?? null;
    const parsed = parseInitData(rawInitData);

    return {
      inTelegram: true,
      rawInitData,
      languageCode: parsed.languageCode,
      startParam: parsed.startParam,
      setupError: null,
    };
  } catch (error) {
    return {
      inTelegram: true,
      rawInitData: null,
      languageCode: undefined,
      startParam: undefined,
      setupError: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Разбор `initData` собственными руками, а не через типы SDK.
 *
 * `initData` — обычная query-строка (`user=<json>&start_param=…&hash=…`), и её форму задаёт
 * Telegram, а не библиотека. Читая её сами, мы не привязываем два поля интерфейса к схеме
 * валидации SDK, которая уже переезжала между версиями.
 *
 * Ошибка разбора здесь безопасна: язык и deep-link — не домен. Подписью занимается бэкенд,
 * и он видит ту же строку целиком.
 */
function parseInitData(raw: string | null): {
  languageCode: string | undefined;
  startParam: string | undefined;
} {
  if (!raw) return { languageCode: undefined, startParam: undefined };

  const params = new URLSearchParams(raw);
  const startParam = params.get('start_param') ?? undefined;

  let languageCode: string | undefined;
  const userJson = params.get('user');
  if (userJson) {
    try {
      const user = JSON.parse(userJson) as { language_code?: unknown };
      if (typeof user.language_code === 'string') languageCode = user.language_code;
    } catch {
      // Локаль просто останется по умолчанию (русской). Логику записи это не трогает.
      languageCode = undefined;
    }
  }

  return { languageCode, startParam };
}

// ── Поделиться, скопировать, скачать ─────────────────────────────────────────
// Вне Telegram (браузер, разработка) методов SDK нет — там работает веб-замена.

export function shareLink(url: string, text: string): void {
  if (shareURL.isAvailable()) shareURL(url, text);
  else window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`);
}

/**
 * Копирование. Сначала `execCommand` синхронно, в пределах нажатия: в webview Telegram
 * `navigator.clipboard` бывает недоступен, и кнопка «Скопировать» молча не делала ничего.
 */
export async function copyText(text: string): Promise<void> {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  area.setSelectionRange(0, text.length);
  const ok = document.execCommand('copy');
  area.remove();
  if (!ok) await copyTextToClipboard(text);
}

/** `url` — абсолютный: Telegram скачивает файл сам, вне webview. */
export async function download(url: string, fileName: string): Promise<void> {
  if (downloadFile.isAvailable()) await downloadFile(url, fileName);
  else window.open(url);
}

/**
 * Счёт Telegram Payments по ссылке из `createInvoiceLink`. Вне Telegram счёт не открыть —
 * возвращается `'unsupported'`, и экран так и говорит, а не делает вид, что оплатил.
 */
export async function payInvoice(url: string): Promise<string> {
  if (!openInvoice.isAvailable()) return 'unsupported';
  // 'paid' | 'cancelled' | 'failed' | 'pending' — по статусу решает экран.
  return openInvoice(url, 'url');
}
